import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Standalone account-deletion page — required by Google Play (must be reachable
 * by reviewers without needing to install the app). Logs in with email+password,
 * then immediately calls DELETE /api/auth/delete-account.
 */
export default function DeleteAccountPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (confirm !== "DELETE") {
      setError("Type DELETE to confirm.");
      return;
    }
    setLoading(true);
    try {
      const loginRes = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      const token = loginRes.data.access_token;
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password },
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete account. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] px-6 py-10" data-testid="delete-account-page">
      <div className="max-w-md w-full mx-auto flex-1 flex flex-col">
        <Link to="/" className="text-sm text-[#6B7280] hover:text-[#1F3A5F] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={16} /> Back to home
        </Link>

        {!done ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle size={26} className="text-[#E07A7A]" />
            </div>
            <h1 className="text-3xl font-bold text-[#1F3A5F] mb-2">Delete your account</h1>
            <p className="text-[#6B7280] mb-6">
              This permanently erases your SayItBetter account and <strong>all associated data</strong> —
              including your message history and settings. This cannot be undone.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
              <strong>What will be deleted:</strong>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Your email, name, and login credentials</li>
                <li>All saved message analyses and history</li>
                <li>Notification preferences</li>
                <li>Password reset tokens</li>
              </ul>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="delete-account-form">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 rounded-xl bg-white"
                  required
                  data-testid="delete-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-white"
                  required
                  data-testid="delete-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">Type <span className="font-mono text-[#E07A7A]">DELETE</span> to confirm</label>
                <Input
                  type="text"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="DELETE"
                  className="h-12 rounded-xl bg-white"
                  data-testid="delete-confirm-input"
                />
              </div>

              {error && (
                <div className="text-sm text-[#E07A7A] bg-[#FEF3F2] border border-[#FECACA] p-3 rounded-xl">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || confirm !== "DELETE"}
                className="w-full h-12 rounded-xl bg-[#E07A7A] hover:bg-[#c56969] text-white font-semibold"
                data-testid="delete-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Permanently delete my account"}
              </Button>
            </form>

            <p className="text-xs text-[#6B7280] mt-6 text-center">
              Questions? Email{" "}
              <a href="mailto:hello@sayitbetter.me" className="text-[#2F5BFF] font-medium">
                hello@sayitbetter.me
              </a>
            </p>
          </>
        ) : (
          <div className="text-center flex-1 flex flex-col justify-center" data-testid="delete-success">
            <div className="w-16 h-16 bg-[#EAF6F4] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-[#4FB3A5]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F3A5F] mb-2">Account deleted</h2>
            <p className="text-[#6B7280] mb-6">
              Your account and all associated data have been permanently removed. We're sorry to see you go.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
